import React, { useState } from 'react';
import { CustomSelect } from '@/components/Formik/CustomSelect';
import NewAddress from '@/components/Modals/ConfirmAddress/NewAddress';
import { IModalStepByStepCard } from '@/components/StepByStepCard';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ICompany } from '@/types/api/companies';
import { handleErrorAsync } from '@/utils/handlers';
import { KeyboardBackspace } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Formik } from 'formik';

interface IAddressDataProps {
  title: string;
  data: Nullable<string>;
}

const AddressData = ({ title, data }: IAddressDataProps) => {
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          mb: '6px',
        }}
      >
        <Typography
          variant="caption"
          color="#808080"
          sx={{ wordBreak: 'normal' }}
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          data-testid={`confirm-address-${title
            .replace(' ', '-')
            .toLowerCase()}`}
        >
          {data || '-'}
        </Typography>
      </Box>
    </>
  );
};

interface IConfirmAddressProps extends IModalStepByStepCard {
  isEditingAddress?: boolean;
}

interface IAddressData {
  companyId: number;
  full_name: string;
  city: Nullable<string>;
  neighbourhood: Nullable<string>;
  street: Nullable<string>;
  zipcode: Nullable<string>;
  state: Nullable<string>;
  complement: Nullable<string>;
  number: Nullable<string>;
}

export default function ConfirmAddress({
  isOpen,
  closeModal,
  onSuccess,
  isEditingAddress,
}: IConfirmAddressProps) {
  const { setIsLoading } = useLoading();
  const { user, updateUserData } = useUser();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [addressData, setAddressData] = useState<IAddressData | null>(null);
  const [openModalNewAddress, setOpenModalNewAddress] = useState(false);

  const handleSelectCompany = (formValues: { companyId: number }) => {
    const companySelected = user.memberships?.find(
      membership => membership.company.id === Number(formValues.companyId)
    )?.company;

    if (!companySelected) return;

    setAddressData({
      companyId: companySelected.id,
      full_name: companySelected.full_name,
      city: companySelected.city,
      neighbourhood: companySelected.neighbourhood,
      state: companySelected.state,
      street: companySelected.street,
      zipcode: companySelected.zipcode,
      complement: companySelected.complement,
      number: companySelected.number,
    });
  };

  const handleValidateAddress = async () => {
    const [companyUpdated, error] = await handleErrorAsync(async () => {
      setIsLoading(true);

      const { data } = await api.put<{ data: ICompany }>(
        `/api/companies/${addressData?.companyId}/`,
        {
          ...addressData,
          full_name: addressData?.full_name,
          has_confirmed_address: true,
        }
      );

      return data;
    }).finally(() => {
      setIsLoading(false);
    });

    if (error) return Toast('error', error.message);

    const membershipsUpdated = user.memberships?.map(membership =>
      membership.company.id === addressData?.companyId
        ? {
            ...membership,
            company: Object.assign(membership.company, companyUpdated),
          }
        : membership
    );
    updateUserData({
      memberships: membershipsUpdated,
    });
    onSuccess();
    Toast('success', 'Endereço confirmado com sucesso!');
  };

  const onClickArrowBack = () => {
    if (addressData) {
      setAddressData(null);
      return;
    }
    closeModal();
  };

  return (
    <>
      <Modal
        aria-labelledby="modal-my-account"
        aria-describedby="modal-my-account-description"
        open={isOpen}
      >
        <>
          <Box
            sx={{
              backgroundColor: '#00A8F3',
              height: '100vh',
              pt: mdUp ? 0 : 4,
            }}
          >
            <Box
              sx={{
                height: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: mdUp ? 0 : '12px 12px 0 0',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  maxWidth: theme.breakpoints.values.md,
                  width: '100%',
                  mx: 'auto',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <IconButton onClick={onClickArrowBack} sx={{ zIndex: 2 }}>
                  <KeyboardBackspace sx={{ color: '#000000' }} />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Confirmar endereço
                </Typography>
              </Box>
              {mdUp && <Divider />}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  flex: 1,
                  maxWidth: 'calc(420px + 32px)',
                  margin: '0 auto',
                  px: 2,
                  width: '100%',
                }}
              >
                {!addressData && user.memberships ? (
                  <Formik
                    initialValues={{
                      companyId: user.memberships[0]?.company.id,
                    }}
                    onSubmit={handleSelectCompany}
                  >
                    {formikProps => (
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          paddingBottom: '38px',
                        }}
                        component="form"
                        onSubmit={formikProps.handleSubmit}
                      >
                        <Box
                          sx={{
                            flex: mdUp ? 0 : 1,
                            display: 'grid',
                            alignItems: 'center',
                            marginBottom: '38px',
                          }}
                        >
                          <Box sx={{ display: 'grid', gap: mdUp ? 3 : 2 }}>
                            <Typography variant="h6" color="text.secondary">
                              Seleciona a empresa que você deseja configurar!
                            </Typography>
                            <CustomSelect
                              name="companyId"
                              id="companyId"
                              placeholder="Sua empresa"
                              sx={{ my: 2, width: '100%' }}
                            >
                              {user?.memberships?.map(membership => {
                                const canEditAddress =
                                  isEditingAddress &&
                                  membership.company.has_confirmed_address &&
                                  !membership.is_refused &&
                                  membership.is_validated &&
                                  (membership.is_superadmin ||
                                    membership.has_confirmed_configuration);

                                const canConfigureAddress =
                                  !isEditingAddress &&
                                  !membership.company.has_confirmed_address &&
                                  !membership.is_refused;

                                if (canEditAddress || canConfigureAddress) {
                                  return (
                                    <option
                                      key={membership.id}
                                      value={membership.company.id}
                                    >
                                      {membership.company.short_name ||
                                        membership.company.full_name}
                                    </option>
                                  );
                                }
                              })}
                            </CustomSelect>
                          </Box>
                        </Box>
                        <Button type="submit" variant="contained" fullWidth>
                          Cadastrar
                        </Button>
                      </Box>
                    )}
                  </Formik>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 2,
                        marginBottom: '38px',
                        flex: mdUp ? 0 : 1,
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{ mb: mdUp ? 3 : 2, fontWeight: '700' }}
                          color="#808080"
                          variant="h5"
                        >
                          Confirme os dados
                        </Typography>

                        <Box>
                          <AddressData
                            title="Endereço"
                            data={addressData?.street}
                          />
                          <AddressData
                            title="Número"
                            data={addressData?.number}
                          />
                          <AddressData
                            title="Bairro"
                            data={addressData?.neighbourhood}
                          />
                          <AddressData
                            title="Cidade"
                            data={`${addressData?.city || ''} - ${
                              addressData?.state || ''
                            }`}
                          />
                          <AddressData
                            title="CEP"
                            data={addressData?.zipcode}
                          />
                          {addressData?.complement && (
                            <AddressData
                              title="Complemento"
                              data={addressData?.complement}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ paddingBottom: '38px' }}>
                      {addressData?.zipcode && (
                        <Button
                          sx={{ mb: 2 }}
                          variant="contained"
                          fullWidth
                          onClick={() => handleValidateAddress()}
                        >
                          continuar com endereço do cnpj
                        </Button>
                      )}

                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setOpenModalNewAddress(true)}
                      >
                        informar um novo endereço
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </>
      </Modal>
      {addressData && (
        <NewAddress
          isOpen={openModalNewAddress}
          addressData={addressData}
          updateAddressData={newAddressData => setAddressData(newAddressData)}
          setIsOpen={() => setOpenModalNewAddress(false)}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
}

export type { IConfirmAddressProps, IAddressData };
